'use client';

import { FC, use } from 'react';

import { AssetUtils } from '@ravago/shared/next-data-access';
import { NormalizedBusinessContext, NormalizedNavigationGroup } from '@ravago/shared/page-data/models/contentful-mapped';
import { ContentfulDocument } from '@ravago/shared/page-elements-radiance/components/shared/contentful-document/contentful-document';
import { Link } from '@ravago/shared/page-elements-radiance/components/shared/link/link';
import { BottomContainerContext } from '@ravago/shared/page-elements-radiance/context/bottom-container-context';
import { AspectRatio } from '@ravago/shared/radiance/components/aspect-ratio/components/AspectRatio/AspectRatio';
import { Box } from '@ravago/shared/radiance/components/box/components/Box/Box';
import { Container } from '@ravago/shared/radiance/components/container/components/Container/Container';
import { Divider } from '@ravago/shared/radiance/components/divider/components/Divider/Divider';
import { Grid } from '@ravago/shared/radiance/components/grid/components/Grid/Grid';
import { GridCol } from '@ravago/shared/radiance/components/grid/components/GridCol/GridCol';
import { Icon } from '@ravago/shared/radiance/components/icon/components/Icon/Icon';
import { Image } from '@ravago/shared/radiance/components/image/components/Image/Image';
import { MultiLineText } from '@ravago/shared/radiance/components/typography/components/MultiLineText/MultiLineText';
import { Typography } from '@ravago/shared/radiance/components/typography/components/Typography/Typography';
import notification from '@ravago/shared/radiance/utils/notification/notification';
import { useTranslations } from '@ravago/shared/translations/client';

import { OleInfo } from '../../../models/contentful/ole-info.model';

interface Props {
  businessContext: NormalizedBusinessContext;
  locale: string;
  acceptWebp?: boolean;
  ole?: OleInfo;
}

const Footer: FC<Props> = ({ businessContext, acceptWebp, locale, ole }: Props) => {
  const translate = useTranslations();

  const { hasBottomSheet } = use(BottomContainerContext);

  const handleOpenOsanoFlyout = () => {
    if ((window as any)?.Osano) {
      (window as any).Osano.cm.showDrawer('osano-cm-dom-info-dialog-open');
    } else {
      notification(translate('page-consumer.technical-support.error.information'), { duration: 3000 });
    }
  };

  const group = (navGroup?: NormalizedNavigationGroup, osanaCookiePreference?: boolean) => (
    <GridCol spanDesktop={2} spanTablet={3} spanPhone={4}>
      {navGroup && (
        <Box direction="column" gap="sm">
          <Typography variant="body-highlight" color="primary">
            {navGroup.label}
          </Typography>
          <Box direction="column" gap="sm">
            {navGroup.items
              .filter((item) => item.externalUrl || item.page?.route)
              .map((navItem) => (
                <Link underline="none" href={navItem.page ? `/${locale}/${navItem.page.route ?? ''}` : navItem.externalUrl ?? undefined} target={navItem.openInNewTab ? '_blank' : undefined} key={navItem.label}>
                  <Typography variant="body">{navItem.label}</Typography>
                </Link>
              ))}
            {osanaCookiePreference && (
              <Link underline="none" onClick={handleOpenOsanoFlyout}>
                <Typography variant="body">{translate('page-consumer.osana-cookie-preference')}</Typography>
              </Link>
            )}
          </Box>
        </Box>
      )}
    </GridCol>
  );

  return (
    <Container>
      <Box direction="column" gap="xl" spacing={{ top: 'xl', bottom: 'xl' }} margin={{ bottom: hasBottomSheet ? '3xl' : 'xl' }}>
        <Grid>
          <GridCol spanDesktop={5} spanTablet={12} spanPhone={4}>
            <Box maxWidth="200px">
              <Link underline="none" href={`/${locale}`}>
                <Image objectFit="contain" src={AssetUtils.optimizeImageIfRequired(businessContext.logo.url, acceptWebp)} alt={businessContext.name} title={businessContext.name} defer />
              </Link>
            </Box>
          </GridCol>
          {group(businessContext.footer[0], false)}
          {group(businessContext.footer[1], true)}
          <GridCol spanDesktop={3} spanTablet={6} spanPhone={4}>
            <Box direction="column" gap="sm">
              <Typography variant="body-highlight" color="primary">
                {ole?.title ?? translate('page-consumer.footer.contact')}
              </Typography>
              {(ole?.phone || businessContext.phoneNr) && (
                <Box gap="2xs" items="baseline">
                  <Typography color="primary">
                    <Icon icon="phone" size="body-highlight" />
                  </Typography>
                  <Link underline="none" href={`tel:${ole?.phone ?? businessContext.phoneNr}`}>
                    <Typography>{ole?.phone ?? businessContext.phoneNr}</Typography>
                  </Link>
                </Box>
              )}
              {(!!ole?.address || businessContext.address) && (
                <Box gap="2xs" items="baseline">
                  <Typography color="primary">
                    <Icon icon="map-marker-alt" size="body-highlight" />
                  </Typography>
                  <Typography>
                    <Box direction="column" gap="none">
                      {ole?.address && <ContentfulDocument document={ole?.address.json} color="primary" />}
                    </Box>
                    {businessContext.address && <MultiLineText text={businessContext.address} lineBreak="\n" />}
                  </Typography>
                </Box>
              )}
              {(ole?.email || businessContext.emailAddress) && (
                <Box gap="2xs" items="baseline">
                  <Typography color="primary">
                    <Icon icon="envelope" size="body-highlight" />
                  </Typography>
                  <Link underline="none" href={`mailto:${ole?.email ?? businessContext.emailAddress}`}>
                    <Typography>{ole?.email ?? businessContext.emailAddress}</Typography>
                  </Link>
                </Box>
              )}
              {!ole && businessContext.linkedInUrl && (
                <Box gap="2xs" items="baseline">
                  <Typography color="primary">
                    <Icon icon="linkedin" type="brand" size="body-highlight" />
                  </Typography>
                  <Link underline="none" href={businessContext.linkedInUrl} target="_blank">
                    <Typography>{translate('page-consumer.footer.follow-us-on-linkedin')}</Typography>
                  </Link>
                </Box>
              )}
            </Box>
          </GridCol>
        </Grid>
        <Divider />
        <Box gap="lg" items="center">
          <AspectRatio aspectRatio="4:3">
            <Image src="/images/footer-logo.png" alt="Ravago" title="Ravago" height="100%" width="100%" objectFit="contain" defer />
          </AspectRatio>
          <Box gap="xs" items="baseline">
            <Icon size="caption" color="subtle" icon="copyright" />
            <Typography variant="caption" color="subtle">
              {translate('page-consumer.footer.legal', { businessContext: businessContext.name })}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Container>
  );
};

export default Footer;
